import { axios } from '@/utils/axios';

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';
export type DeliveryStatus = 'queued' | 'sending' | 'submitted' | 'failed' | 'unknown' | 'cancelled';
export type ShareTargetType = 'document' | 'folder';
export type AccessMode = 'member' | 'external';
export interface DocumentShareInvitation {
  id: number;
  email: string;
  status: InvitationStatus;
  delivery_status: DeliveryStatus;
  expires_at: string;
  resend_available_at: string | null;
  access_mode?: AccessMode;
  download_count?: number;
  download_limit?: number | null;
  last_accessed_at?: string | null;
}
/** Limits come from the backend configuration; the UI must not hard-code them. */
export interface ExternalShareLimits {
  enabled: boolean;
  expiry_days: number;
  download_limit: number | null;
  session_minutes: number;
  code_minutes: number;
  member_expiry_days: number;
}
export interface InvitationDetails {
  status: InvitationStatus;
  target_type: ShareTargetType;
  target_name: string | null;
  expires_at: string;
  share_id: number | null;
}
export interface ExternalInvitationDetails {
  access_mode: 'external';
  status: InvitationStatus;
  masked_email: string;
  expires_at: string;
  locked_until: string | null;
  limits: ExternalShareLimits;
}
export interface ExternalSharedItem {
  name: string | null;
  extension: string | null;
  size: number | null;
  expires_at: string;
  download_count: number;
  download_limit: number | null;
  downloads_remaining: number | null;
  limits: ExternalShareLimits;
}
type Response<T> = { success: boolean; data: T };
export const createDocumentShareInvitation = (data: { target_type: ShareTargetType; target_id: number; email: string; locale: string; access_mode?: AccessMode }) =>
  axios.post<Response<DocumentShareInvitation>>('/api/share-invitations', data);
export const listDocumentShareInvitations = (target_type: ShareTargetType, target_id: number, page = 1) =>
  axios.get<Response<{ items: DocumentShareInvitation[]; current_page: number; last_page: number; external?: ExternalShareLimits }>>('/api/share-invitations', { params: { target_type, target_id, page } });
export const resendDocumentShareInvitation = (id: number) => axios.post<Response<DocumentShareInvitation>>(`/api/share-invitations/${id}/resend`);
export const revokeDocumentShareInvitation = (id: number) => axios.delete<Response<DocumentShareInvitation>>(`/api/share-invitations/${id}`);
export const resolveDocumentShareInvitation = (token: string) => axios.post<Response<InvitationDetails>>('/api/share-invitations/resolve', { token });
export const acceptDocumentShareInvitation = (token: string) => axios.post<Response<InvitationDetails>>('/api/share-invitations/accept', { token });

// External recipients: no account. Only a masked email is visible until a mailed code is verified.
export const EXTERNAL_SHARE_SESSION_HEADER = 'X-Share-Invitation-Session';
export const resolveExternalShareInvitation = (token: string) =>
  axios.post<Response<ExternalInvitationDetails>>('/api/public/share-invitations/resolve', { token });
export const requestExternalShareAccessCode = (token: string) =>
  axios.post<Response<{ expires_at: string; resend_available_at: string | null }>>('/api/public/share-invitations/access-code', { token });
export const verifyExternalShareAccessCode = (token: string, code: string) =>
  axios.post<Response<{ access_session_token: string; expires_in: number }>>('/api/public/share-invitations/verify', { token, code });
export const getExternalSharedItem = (session: string) =>
  axios.get<Response<ExternalSharedItem>>('/api/public/share-invitations/item', { headers: { [EXTERNAL_SHARE_SESSION_HEADER]: session } });

export type InvitationErrorKey = 'self' | 'cooldown' | 'rateLimit' | 'wrongAccount' | 'unverified' | 'alreadyAccepted' | 'unavailable'
  | 'signInRequired' | 'generic' | 'externalDisabled' | 'externalFolder' | 'modeConflict' | 'externalMode'
  | 'codeInvalid' | 'codeExpired' | 'codeCooldown' | 'codeUnavailable' | 'locked' | 'sessionExpired' | 'downloadLimit';
export function invitationErrorKey(error: unknown): InvitationErrorKey {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
  const known: Record<string, InvitationErrorKey> = {
    SHARE_INVITATION_SELF: 'self', SHARE_INVITATION_COOLDOWN: 'cooldown', SHARE_INVITATION_RATE_LIMIT: 'rateLimit',
    SHARE_INVITATION_ACCOUNT_MISMATCH: 'wrongAccount', SHARE_INVITATION_EMAIL_UNVERIFIED: 'unverified',
    SHARE_INVITATION_ALREADY_ACCEPTED: 'alreadyAccepted', SHARE_INVITATION_UNAVAILABLE: 'unavailable',
    SHARE_INVITATION_EXTERNAL_DISABLED: 'externalDisabled', SHARE_INVITATION_EXTERNAL_FOLDER: 'externalFolder',
    SHARE_INVITATION_MODE_CONFLICT: 'modeConflict', SHARE_INVITATION_EXTERNAL_MODE: 'externalMode',
    SHARE_INVITATION_CODE_INVALID: 'codeInvalid', SHARE_INVITATION_CODE_EXPIRED: 'codeExpired',
    SHARE_INVITATION_CODE_COOLDOWN: 'codeCooldown', SHARE_INVITATION_CODE_UNAVAILABLE: 'codeUnavailable',
    SHARE_INVITATION_LOCKED: 'locked', SHARE_INVITATION_SESSION_EXPIRED: 'sessionExpired',
    SHARE_INVITATION_DOWNLOAD_LIMIT: 'downloadLimit',
  };
  return known[response?.data?.message || ''] || (response?.status === 401 ? 'signInRequired' : response?.status === 404 || response?.status === 410 ? 'unavailable' : response?.status === 429 ? 'rateLimit' : 'generic');
}
